import { useEffect, useMemo, useState } from "react";
import { Spin } from "antd";
import { Trans } from "react-i18next";
import classNames from "classnames";
import { useAppDispatch } from "@hooks/redux";
import { fetchProductList } from "@pages/library/librarySlice";
import { formatProductData } from "@pages/aiStudio/components/ProductLibraryModal";
import { formatImgUrl } from "@utils/format";
import Image from "@components/Image";
import TruncateTitle from "@components/TruncateTitle";
import type { SelectedProductPayload } from "./types";

interface SavedProductRecord {
  id: string;
  productTitle: string;
  productCategory?: string;
  coverImageUrl?: string;
  country?: string;
  priCateName?: string | null;
  secCateName?: string | null;
  terCateName?: string | null;
}

export interface SavedProductsPanelProps {
  onSelect: (payload: SelectedProductPayload) => void;
  enrichingId?: string | null;
}

/**
 * 「Add From Library」→ Saved tab：用户在 Kalodata 收藏的商品列表。
 *
 * 设计稿节点：4855:12775。
 */
export const SavedProductsPanel = ({ onSelect, enrichingId }: SavedProductsPanelProps) => {
  const dispatch = useAppDispatch();
  const [list, setList] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    dispatch(fetchProductList())
      .then((result: any) => {
        setList(result.payload ?? []);
      })
      .finally(() => setLoading(false));
  }, [dispatch]);

  const records = useMemo<SavedProductRecord[]>(
    () => list.map((item) => formatProductData(item) as SavedProductRecord),
    [list],
  );

  if (loading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <Spin />
      </div>
    );
  }

  if (records.length === 0) {
    return (
      <div className="flex min-h-[400px] items-center justify-center text-[14px] text-kds-fg-soft">
        <Trans>Nothing here yet.</Trans>
      </div>
    );
  }

  return (
    <div className="kds-scroll-thin grid max-h-[60vh] min-h-[400px] grid-cols-4 gap-[16px] overflow-y-auto px-[16px] py-[12px]">
      {records.map((record) => {
        const isSelected = selectedId === record.id;
        const isEnriching = enrichingId === record.id;
        return (
          <div key={record.id} className="relative">
            <div
              className={classNames(
                "overflow-hidden rounded-[8px] border border-kds-border bg-kds-bg-card transition-colors",
                enrichingId ? "cursor-not-allowed" : "cursor-pointer hover:border-kds-accent",
              )}
              onClick={() => {
                if (enrichingId) return;
                setSelectedId(record.id);
                onSelect({
                  origin: "library-saved",
                  productId: record.id,
                  raw: record,
                });
              }}
            >
              <Image
                url={record.coverImageUrl || formatImgUrl("product", record.id)}
                className="aspect-square w-full rounded-none! border-none! object-cover"
              />
              <div className="px-[10px] pb-[12px] pt-[10px]">
                <TruncateTitle
                  className="h-[36px] text-[13px] leading-[18px] text-kds-fg"
                  text={record.productTitle}
                  lineclamp2
                  placement="top"
                />
              </div>
            </div>
            {isEnriching && (
              <div className="absolute inset-0 z-20 flex items-center justify-center rounded-[8px] bg-black/30">
                <Spin size="small" />
              </div>
            )}
            <div
              className={classNames(
                "absolute -top-[2px] -right-[2px] -bottom-[2px] -left-[2px] z-10 rounded-[10px] border-2 transition-colors pointer-events-none",
                isSelected ? "border-kds-accent!" : "border-transparent",
              )}
              aria-hidden={!isSelected}
            />
          </div>
        );
      })}
    </div>
  );
};
